import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaCheck, FaUndoAlt, FaUpload } from 'react-icons/fa';
import U2000BasicDialog from '../../../../u2000Core/components/dialog/U2000BasicDialog';
import { useToast } from '../../../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../../../U3141';
import type { U3141ImportChainAnnotationsDryRunLot } from '../../../U3141Service';
import {
  u3141BuildDefaultOverwriteByLotIdForDryRun,
  u3141CollectLotDetailIdsToReplace,
} from '../../../tools/u3141ImportChainAnnotationsHelpers';
import { U3141DocumentFileUpload } from '../u3141ChildLotChain/U3141DocumentFileUpload';

const MAX_CHAIN_PDF_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const cancelButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
  active:bg-gray-100 active:scale-95
  disabled:cursor-not-allowed disabled:opacity-60`;
const primaryButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg shadow-xs transition-all duration-200
  hover:bg-green-700 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2
  active:bg-green-800 active:scale-95
  disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none`;

type WizardStep = 'upload' | 'selection';

export interface U3141ImportChainAnnotationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  masterLotChainId: string | undefined;
  onDryRun: (file: File) => Promise<void>;
  onApply: (sourceDocumentId: string, lotDetailIdsToReplace: string[]) => Promise<void>;
  dryRunLots: U3141ImportChainAnnotationsDryRunLot[] | null;
  dryRunSourceDocumentId: string | null;
  dryRunLoading?: boolean;
  applyLoading?: boolean;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'identical':
      return 'bg-gray-100 text-gray-800';
    case 'different':
      return 'bg-amber-100 text-amber-900';
    case 'missing_index':
      return 'bg-red-100 text-red-800';
    case 'no_shapes_in_pdf':
      return 'bg-sky-100 text-sky-900';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export const U3141ImportChainAnnotationsDialog: React.FC<U3141ImportChainAnnotationsDialogProps> = ({
  isOpen,
  onClose,
  masterLotChainId,
  onDryRun,
  onApply,
  dryRunLots,
  dryRunSourceDocumentId,
  dryRunLoading = false,
  applyLoading = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [step, setStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [overwriteByLotId, setOverwriteByLotId] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setFile(null);
      setFormResetKey((k) => k + 1);
      setOverwriteByLotId({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (dryRunLots && dryRunSourceDocumentId) {
      setStep('selection');
      setOverwriteByLotId(u3141BuildDefaultOverwriteByLotIdForDryRun(dryRunLots));
    }
  }, [dryRunLots, dryRunSourceDocumentId]);

  const selectedCount = useMemo(
    () => u3141CollectLotDetailIdsToReplace(overwriteByLotId).length,
    [overwriteByLotId],
  );

  const busy = dryRunLoading || applyLoading;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleUploadSubmit = async () => {
    if (!file || !masterLotChainId || dryRunLoading) return;
    if (file.size > MAX_CHAIN_PDF_FILE_SIZE_BYTES) {
      toast.error(t('msgDocumentFileTooLarge', { ns: NS }));
      return;
    }
    await onDryRun(file);
  };

  const handleApply = async () => {
    if (!dryRunSourceDocumentId || !masterLotChainId || applyLoading) return;
    const lotDetailIdsToReplace = u3141CollectLotDetailIdsToReplace(overwriteByLotId);
    if (lotDetailIdsToReplace.length === 0) return;
    await onApply(dryRunSourceDocumentId, lotDetailIdsToReplace);
  };

  const toggleOverwrite = (lotDetailId: string, checked: boolean) => {
    setOverwriteByLotId((prev) => ({ ...prev, [lotDetailId]: checked }));
  };

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t('dlgImportChainAnnotationsTitle', { ns: NS })}
      width="large"
      height="max-h-[90vh]"
    >
      <div className="flex flex-col h-full focus:outline-hidden">
        <section className="flex-1 px-6 py-4 overflow-y-auto min-h-0">
          {step === 'upload' ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-700">{t('dlgImportChainAnnotationsUploadMessage', { ns: NS })}</p>
              <U3141DocumentFileUpload
                key={formResetKey}
                label={t('lblChainPdfFile', { ns: NS })}
                value={file}
                onChange={setFile}
                accept=".pdf,application/pdf"
                disabled={busy}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-700">{t('dlgImportChainAnnotationsSelectionMessage', { ns: NS })}</p>
              {dryRunLoading ? (
                <p className="text-sm text-gray-500">{t('msgImportChainAnnotationsDryRunLoading', { ns: NS })}</p>
              ) : null}
              {dryRunLots && dryRunLots.length === 0 ? (
                <p className="text-sm text-gray-500">{t('msgImportChainAnnotationsNoLots', { ns: NS })}</p>
              ) : null}
              {dryRunLots && dryRunLots.length > 0 ? (
                <div className="overflow-x-auto rounded-sm border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colImportChainAnnotationsLot', { ns: NS })}</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colImportChainAnnotationsStatus', { ns: NS })}</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">{t('colExistingShapes', { ns: NS })}</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">{t('colExtractedShapes', { ns: NS })}</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600">{t('lblImportChainAnnotationsOverwrite', { ns: NS })}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {dryRunLots.map((lot) => (
                        <tr key={lot.lotDetailId}>
                          <td className="px-3 py-2 text-gray-800">{lot.lotLabel}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(lot.status)}`}
                            >
                              {t(`lblImportChainAnnotationsStatus.${lot.status}`, {
                                ns: NS,
                                defaultValue: lot.status,
                              })}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{lot.existingShapeCount}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{lot.extractedShapeCount}</td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                              checked={Boolean(overwriteByLotId[lot.lotDetailId])}
                              onChange={(e) => toggleOverwrite(lot.lotDetailId, e.target.checked)}
                              disabled={busy}
                              aria-label={t('lblImportChainAnnotationsOverwriteForLot', {
                                ns: NS,
                                lot: lot.lotLabel,
                              })}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          )}
        </section>
        <footer className="flex items-center justify-between gap-2 border-t border-gray-200 px-6 py-4">
          <div>
            {step === 'selection' ? (
              <button
                type="button"
                className={cancelButtonClass}
                onClick={() => {
                  if (busy) return;
                  setStep('upload');
                  setOverwriteByLotId({});
                }}
                disabled={busy}
              >
                <FaArrowLeft className="h-4 w-4" />
                {t('btnImportChainAnnotationsBack', { ns: NS })}
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className={cancelButtonClass} onClick={handleClose} disabled={busy}>
              <FaUndoAlt className="h-4 w-4" />
              {t('dlgLblCancel', { ns: NS })}
            </button>
            {step === 'upload' ? (
              <button
                type="button"
                className={primaryButtonClass}
                onClick={() => void handleUploadSubmit()}
                disabled={!file || !masterLotChainId || busy}
              >
                <FaUpload className="h-4 w-4" />
                {dryRunLoading
                  ? t('btnImportChainAnnotationsDryRunRunning', { ns: NS })
                  : t('btnImportChainAnnotationsDryRun', { ns: NS })}
              </button>
            ) : (
              <button
                type="button"
                className={primaryButtonClass}
                onClick={() => void handleApply()}
                disabled={!dryRunSourceDocumentId || selectedCount === 0 || busy}
              >
                <FaCheck className="h-4 w-4" />
                {applyLoading
                  ? t('btnImportChainAnnotationsApplyRunning', { ns: NS })
                  : t('btnImportChainAnnotationsApply', { ns: NS })}
              </button>
            )}
          </div>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
